import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { FILE_EXTENSION_IMAGES } from '@crehana/compass.knowledgehub';
import { VideoLectureTypeEnum } from '@crehana/compass.knowledgehub/types';

import { DocumentActionEnum } from '@/shared/types/graphql/globals.v5';

import GET_COURSE_DETAIL_SERVER_COURSE_QUERY from '../graphql/GetCourseDetailServerCourseQuery.v5.graphql';
import type {
  GetCourseDetailServerCourseQuery,
  GetCourseDetailServerCourseQueryVariables,
} from '../graphql/types/GetCourseDetailServerCourseQuery';
import type { TCourse, TDocumentType } from '../types';
import trailerTypeToMimeType from '../utils/trailerTypeToMimeType';

export interface TUseKnowledgeHubCourseArgs {
  variables: {
    organizationId: string;
    contentSlug?: string;
    contentId?: number;
  };
  skip?: boolean;
}

const VIDE_TYPE_MAP: Readonly<Record<string, VideoLectureTypeEnum>> = {
  quiz: VideoLectureTypeEnum.VideoLectureQuiz,
  class: VideoLectureTypeEnum.VideoLectureClass,
  break: VideoLectureTypeEnum.VideoLectureBreak,
  exercise: VideoLectureTypeEnum.VideoLectureExercise,
  scorm: VideoLectureTypeEnum.VideoLectureScorm,
  pdf: VideoLectureTypeEnum.VideoLecturePdf,
  iframe: VideoLectureTypeEnum.VideoLectureIframe,
  survey: VideoLectureTypeEnum.VideoLectureSurvey,
  live_session: VideoLectureTypeEnum.VideoLectureLiveSession,
};

export function useKnowledgeHubCourse(args: TUseKnowledgeHubCourseArgs) {
  const isSkip = args.skip || args.variables.contentSlug === '';
  const { data, loading, error } = useQuery<
    GetCourseDetailServerCourseQuery,
    GetCourseDetailServerCourseQueryVariables
  >(GET_COURSE_DETAIL_SERVER_COURSE_QUERY, {
    variables: {
      organization_id: args.variables.organizationId,
      content_slug: args.variables.contentSlug,
      content_id: args.variables.contentId,
    },
    context: {
      clientName: 'v5',
    },
    skip: isSkip,
  });

  const course = useMemo<TCourse | undefined>(() => {
    const courseDetail = data?.knowledge_hub_content_detail;
    const nodeCourse = courseDetail?.course;

    if (courseDetail?.original_id && nodeCourse) {
      const totalLessons: number =
        nodeCourse.modules.reduce(
          (acc, module) => acc + module.videos.length,
          0,
        ) || 0;
      const levelObject: TCourse['level'] = nodeCourse?.course_level
        ? {
            name: nodeCourse.course_level.name,
          }
        : undefined;

      return {
        originalId: courseDetail.original_id,
        title: nodeCourse.title || '',
        description: nodeCourse.description || '',
        professorName: courseDetail.professor_name || '',
        professor: courseDetail.course?.professor
          ? {
              fullName: courseDetail.course?.professor?.full_name || '',
              occupation: courseDetail.course?.professor?.occupation || '',
              profilePictureUrl:
                courseDetail.course?.professor?.profile_picture_url?.url || '',
            }
          : undefined,
        metaTitle: nodeCourse.title || '',
        metaDescription: nodeCourse.description || '',
        url: courseDetail.enrollment_url || '',
        promoImage: nodeCourse.image || '',
        duration: nodeCourse.duration || 0,
        totalLessons: totalLessons,
        totalAttachments: nodeCourse.total_attachments_number,
        enrollments: 0,
        modifiedDate: courseDetail.modified_date || '',
        level: levelObject,
        isMandatory: nodeCourse.is_mandatory,
        courseModules: nodeCourse.modules.map(module => ({
          id: module.id,
          originalId: module.id,
          order: module.order,
          name: module.name,
          duration: module.videos.reduce(
            (acc, video) => acc + video.duration,
            0,
          ),
          videoLectureSet: module.videos.map(video => ({
            id: video.id,
            originalId: video.id,
            order: video.order,
            title: video.title,
            duration: video.duration,
            videoTypeEnum: parseVideoTypeToEnum(video.video_type || ''),
          })),
        })),
        videoTrailer: nodeCourse.video_trailer?.video_url
          ? {
              sources: [
                {
                  type: trailerTypeToMimeType(
                    nodeCourse.video_trailer.video_source_type,
                  ),
                  src: nodeCourse.video_trailer.video_url,
                },
              ],
            }
          : undefined,
        requirements: courseDetail.course?.requirements || null,
      } satisfies TCourse;
    }

    return undefined;
  }, [data]);

  const document = useMemo<TDocumentType | undefined>(() => {
    const nodeDetail = data?.knowledge_hub_content_detail;
    const nodeDocument = nodeDetail?.document;

    // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
    if (!nodeDocument) return undefined;

    const extension = nodeDocument.extension?.toLocaleLowerCase() || '';
    const coverImage = extension ? FILE_EXTENSION_IMAGES[extension] : '';
    return {
      id: nodeDetail.original_id || '',
      name: nodeDocument.name,
      size: nodeDocument.size || '',
      description: nodeDocument.description || '',
      canPreview: nodeDocument.action === DocumentActionEnum.PREVIEW,
      extension,
      coverImage,
      downloadUrl: nodeDocument.document_file?.file?.download_url || '',
      categoriesList: nodeDetail.related_categories || [],
      updatedAt: nodeDocument.updated_at
        ? new Date(nodeDocument.updated_at)
        : undefined,
    };
  }, [data]);

  return {
    course,
    document,
    loading,
    error,
  };
}

// TODO: Remove this function when the 'video_type_enum' is added to the API.
const parseVideoTypeToEnum = (type: string): VideoLectureTypeEnum =>
  VIDE_TYPE_MAP[type] || VideoLectureTypeEnum.VideoLectureClass;

export default useKnowledgeHubCourse;
